import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import ProfileIcon from '../../../assets/icons/Profile.svg';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../auth/AuthContext';
import UserService from '../../../services/UserService';
import AlertService from '../../../services/AlertService';
import MaleIcon from '../../../assets/icons/Man.svg';
import WomanIcon from '../../../assets/icons/Woman.svg';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact';
import OtpIcon from '../../../assets/icons/TwoFactorIcon.png';

const EditProfile = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState();
  const [email, setEmail] = useState();
  const [mobileNo, setMobileNo] = useState();
  const [profileImage, setProfileImage] = useState();
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState({});
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const fileInputRef = useRef(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const otpControllerRef = useRef(null);

  // OTP management
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [otpLoading, setOtpLoading] = useState(false);

  const authContext = useContext(AuthContext);

const loadUserInfo = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await UserService.GetProfile(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setDisplayName(data.data.displayName);
        setEmail(data.data.email);
        setMobileNo(data.data.mobileNo);
        setProfileImage(data.data.gender === 'F' ? WomanIcon : MaleIcon);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

 useEffect(() => {
    const controller = new AbortController();
    if (showEditProfile) loadUserInfo(controller.signal);
    return () => controller.abort(); 
  }, [showEditProfile, loadUserInfo]);

  const handleOtpChange = (e, index) => {
    let value = e.target.value.replace(/\D/, '');
    if (value.length > 1) value = value.charAt(0);

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('Text').slice(0, 6).split('');
    setOtp((prev) => prev.map((_, i) => pasted[i] || ""));
  };

  const handleImageUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        setShowUploadDialog(false);
      };
      reader.readAsDataURL(file);
    }
  };



const handleOtpSubmit = async (event) => {
  event.preventDefault();
  const codeValue = otp.join("");
  if (!codeValue) return;

  if (otpControllerRef.current) {
    otpControllerRef.current.abort();
  }
  const controller = new AbortController();
  otpControllerRef.current = controller;

  setOtpLoading(true);

  try {
    const result = await UserService.VerifyQrCode(
      codeValue,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (result.isSuccess) {
      setShowEditProfile(true);
      setOtp(new Array(6).fill(""));
    } else {
      AlertService.error(result.errorMessage || "Invalid code");
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("Internal error");
  } finally {
    if (!controller.signal.aborted) setOtpLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    otpControllerRef.current?.abort();
  };
}, []);

  const dialogFooter = (
    <div className="d-flex justify-content-end gap-2">
      <Button
        onClick={() => setShowUploadDialog(false)}
        className="p-button-text"
      >
        <i className='pi pi-times'></i> Cancel
      </Button>
    </div>
  );

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleImageUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

 const onSubmitHandler = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      let payload = [
        { key: "DisplayName", value: displayName },
        { key: "Email", value: email },
        { key: "MobileNo", value: mobileNo },
      ];
      let data = await UserService.UpdateProfile(payload, authContext);
      if (data.isSuccess) {
        navigate("/profile");
      } else {
        AlertService.error(data.errorMessage);
      }
      setLoading(false);
    } catch (ex) {
      console.log(ex);
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="d-flex profile container-fluid mt-4 pt-3">
        {!showEditProfile ? (
          <Fragment>
            {/* Left Side Image */}
            <div className="OtpVerify-left w-50 d-none d-xl-block d-flex justify-content-center align-items-center">
              <img src={OtpIcon} alt="Profile" className="img-fluid" />
            </div>

            {/* Right Side Content */}
            <div className="d-flex flex-column justify-content-center mx-auto mt-5 pt-5 mt-xl-0 pt-xl-0" style={{ maxWidth: "550px", width: "100%" }}>
              {otpLoading ? (
                <>
                  <center>
                    <h4 className="mb-4 text-center mb-2">
                      <Skeleton width="60%" height="3rem" />
                    </h4>
                  </center>

                  <div className="mb-4 text-center">
                    <Skeleton width="100%" height="3rem" className="mb-2" />
                    <Skeleton width="100%" height="3rem" className="mx-auto" />
                  </div>
                  <div className="d-flex justify-content-center mb-4 gap-3">
                    {[...Array(6)].map((_, i) => (
                      <Skeleton
                        key={i}
                        width="3rem"
                        height="3rem"
                        className="rounded"
                      />
                    ))}
                  </div>
                  <div className="d-flex justify-content-between mt-5">
                    <Skeleton width="30%" height="3.5rem" />
                    <Skeleton width="30%" height="3.5rem" />
                  </div>
                </>
              ) : (
                <>
                  <h4 className="mb-4 text-center text-xl-start ">Two Factor Verification</h4>
                  <p className="mb-4 text-center text-xl-start">
                    Please provide the One-Time Password code generated by{' '}
                    <strong>Microsoft Authentication</strong> or any{' '}
                    <strong>Authentication</strong> app’s in the below textbox
                  </p>

                  <Form onSubmit={handleOtpSubmit}>
                    <div
                      className="d-flex justify-content-center mb-4 gap-3"
                      onPaste={handlePaste}
                    >
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          id={`otp-${i}`}
                          type="text"
                          maxLength="1"
                          inputMode="numeric"
                          className="form-control text-center otp-input w-100 custom-input-color"
                          value={digit}
                          onChange={(e) => handleOtpChange(e, i)}
                          onKeyDown={(e) => handleOtpKeyDown(e, i)}
                          onFocus={(e) => e.target.select()}
                          aria-label={`OTP digit ${i + 1}`}
                          autoComplete="off"
                          style={{ width: "3rem", height: "3rem" }}
                          autoFocus={i === 0} 
                        />
                      ))}
                    </div>

                    <div className="d-flex justify-content-between mt-4 pt-2">
                      <Button
                        variant="outline-secondary"
                        className="fw-bold px-lg-5 py-2"
                        onClick={() => navigate("/profile")}
                        disabled={otpLoading}
                        type="button"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        className="ms-auto px-lg-5 py-2 d-flex align-items-center justify-content-center"
                        disabled={otpLoading}
                        type="submit"
                      >
                        Verify
                      </Button>
                    </div>
                  </Form>
                </>
              )}
            </div>
          </Fragment>

        ) : (
          <Fragment>
            <div className='profile-left mx-auto d-none d-xl-block'>
              <img src={ProfileIcon} alt='Profile' className='img-fluid' />
            </div>
            <div className='profile-right mt-xl-5 pt-5'>
              {loading ? (
                <div className='mt-auto mx-auto' style={{ maxWidth: '600px' }}>
                  <center>
                    <h4>Profile Details</h4>
                  </center>
                  <div className="d-flex justify-content-center mb-3 mt-3">
                    <div className="relative group">
                      <Skeleton size='9rem' shape='circle' />
                    </div>
                  </div>
                  <div className="mb-2">
                    <Skeleton width='25%' height='2rem' />
                  </div>
                  <div className="mb-4">
                    <Skeleton width='100%' height='3.5rem' />
                  </div>
                  <div className="mb-2">
                    <Skeleton width='20%' height='2rem' />
                  </div>
                  <div className="mb-4">
                    <Skeleton width='100%' height='3.5rem' />
                  </div>
                  <div className="mb-2">
                    <Skeleton width='25%' height='2rem' />
                  </div>
                  <div className="mb-4">
                    <Skeleton width='100%' height='3.5rem' />
                  </div>
                  <div className="mt-5 d-flex">
                    <Skeleton width='20%' height='2.5rem' />
                    <Skeleton width='20%' height='2.5rem' className='ms-auto' />
                  </div>
                </div>
              ) : (
                <Form className='mt-auto mx-auto ' onSubmit={onSubmitHandler} style={{ maxWidth: '600px' }}>
                  <center>
                    <h4>Profile Details</h4>
                  </center>
                  <div className='mb-4'>
                    <div className="d-flex justify-content-center">
                      <div
                        className='profile-image-container'
                        style={{ position: 'relative', cursor: 'pointer' }}
                        onClick={() => setShowUploadDialog(true)}
                      >
                        <img
                          src={profileImage}
                          alt="Profile"
                          className='profile-image'
                          style={{
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '4px solid white',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.3s ease'
                          }}
                        />
                        <div
                          className="camera-overlay"
                          style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: '0',
                            transition: 'opacity 0.3s ease',
                            color: 'white',
                            fontSize: '1.5rem'
                          }}
                        >
                          <i className="pi pi-camera" />
                        </div>
                      </div>
                    </div>
                    <center>
                      <small style={{ fontSize: '10px' }}>Click on the image to change photo</small>
                    </center>
                  </div>
                  <Form.Group className="mb-4">
                    <Form.Label className="d-flex align-items-center">
                      Display Name
                    </Form.Label>
                    <Form.Control
                      className={`custom-input custom-input-color`}
                      type="text"
                      placeholder="Enter Display Name"
                      onChange={(e) => {
                        setDisplayName(e.target.value);
                        setErrors({ ...errors, displayName: undefined });
                      }}
                      value={displayName}
                    />
                    {errors.displayName && <div className="invalid-feedback d-block">{errors.displayName}</div>}
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label className="d-flex align-items-center">
                      Email
                    </Form.Label>
                    <Form.Control
                      className={`custom-input custom-input-color`}
                      type="text"
                      placeholder="Enter Email"
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors({ ...errors, email: undefined });
                      }}
                      value={email}
                    />
                    {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label className="d-flex align-items-center">
                      Mobile Number
                    </Form.Label>
                    <Form.Control
                      className={`custom-input custom-input-color`}
                      type="text"
                      placeholder="Enter Mobile Number"
                      onChange={(e) => {
                        setMobileNo(e.target.value);
                        setErrors({ ...errors, mobileNo: undefined });
                      }}
                      value={mobileNo}
                    />
                    {errors.mobileNo && <div className="invalid-feedback d-block">{errors.mobileNo}</div>}
                  </Form.Group>
                  <div className="d-flex gap-4">
                    <Button
                      variant="outline-secondary"
                      className="fw-bold px-lg-5 py-2"
                      onClick={() => navigate("/profile")}
                      disabled={loading}
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="ms-auto px-lg-5 py-2 d-flex align-items-center justify-content-center"
                      disabled={loading}
                      type="submit"
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
              )}
              <Dialog
                header="Update Profile Picture"
                visible={showUploadDialog}
                style={{ width: '450px' }}
                footer={dialogFooter}
                onHide={() => setShowUploadDialog(false)}
                modal
                draggable={false}
                resizable={false}
              >
                <div
                  className="text-center p-4"
                  style={{
                    border: `2px dashed ${isDragging ? '#0d6efd' : '#dee2e6'}`,
                    borderRadius: '12px',
                    backgroundColor: isDragging ? '#e3f2fd' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={triggerFileInput}
                >
                  <i
                    className="pi pi-cloud-upload"
                    style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem', display: 'block' }}
                  />
                  <p style={{ color: '#6c757d', marginBottom: '0.5rem' }}>Drag & drop an image here</p>
                  <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>or</p>
                  <Button
                    label="Choose File"
                    icon="pi pi-upload"
                    onClick={triggerFileInput}
                  >
                    <i className='pi pi-upload'></i>
                  </Button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                <p style={{ fontSize: '0.75rem', color: '#6c757d', textAlign: 'center', marginTop: '1rem', marginBottom: '0' }}>
                  Supports JPG, PNG, GIF up to 5MB
                </p>
              </Dialog>
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default EditProfile;
